import { DetailSection } from '@/components/admin/detail-parts';
import { type ModerationItem } from '@/contracts';
import { useTranslations } from '@/i18n/use-locale';
import { isoDate } from '@/lib/detail';
import { useModerationDecisionLabel } from '@/lib/labels';

/**
 * Every decision on record, oldest first, as a timeline. Once the item is decided its own
 * decision is the last entry; the header's status pill is the only other place the
 * outcome appears, so nothing here restates it.
 */
export function HistorySection({ item }: { item: ModerationItem }) {
  const t = useTranslations('admin.review');
  const decisionLabel = useModerationDecisionLabel();
  const history = item.history ?? [];

  return (
    <DetailSection title={t('section.history')}>
      {history.length === 0 ? (
        <p className="text-[12.5px] text-conc-400">{t('history.empty')}</p>
      ) : (
        <ol
          aria-label={t('section.history')}
          className="relative ms-1.5 space-y-4 border-s border-conc-100"
        >
          {history.map((entry, index) => (
            <li
              key={`${entry.decidedAt}-${index}`}
              data-decision={entry.decision}
              className="relative ps-5 text-[12.5px]"
            >
              <span
                aria-hidden
                className={`absolute -start-[5px] top-1.5 h-2.5 w-2.5 rounded-full ${
                  entry.decision.startsWith('approve') ? 'bg-pitch' : 'bg-rose-500'
                }`}
              />
              <p className="flex flex-wrap items-center gap-2">
                <span className="font-bold text-navy">{decisionLabel(entry.decision)}</span>
                <time dateTime={entry.decidedAt} className="num text-conc-500">
                  {isoDate(entry.decidedAt)}
                </time>
                {entry.decidedBy && (
                  <span className="text-conc-500">
                    {t('history.by', { name: entry.decidedBy })}
                  </span>
                )}
              </p>
              {entry.reason && (
                <p dir="auto" className="mt-1 text-conc-600">
                  {entry.reason}
                </p>
              )}
            </li>
          ))}
        </ol>
      )}
    </DetailSection>
  );
}
